
import React from 'react';
import { QuickEditAction } from '../types';
import LoadingSpinner from './LoadingSpinner';
import { formatImageSuffix } from '../App'; 

interface QuickEditPanelProps {
  quickEditActions: QuickEditAction[];
  onApplyQuickEdit: (action: QuickEditAction) => void;
  isProcessing: boolean;
  selectedImageName: string | null;
  selectedImageParsedDate?: string;
  selectedImageParsedTime?: string;
  selectedImageParsedMachineCode?: string;
  selectedImageDescription: string | null; 
  onDebugHover?: (name: string | null) => void; // New prop
}

const QuickEditPanel: React.FC<QuickEditPanelProps> = ({
  quickEditActions,
  onApplyQuickEdit,
  isProcessing,
  selectedImageName,
  selectedImageParsedDate,
  selectedImageParsedTime,
  selectedImageParsedMachineCode,
  selectedImageDescription,
  onDebugHover,
}) => {
  const selectedOriginalImageSubject = selectedImageDescription || selectedImageName;
  const quickEditButtonClasses = "px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-xs sm:text-sm";

  const getSelectedImageDisplayNameWithSuffix = () => {
    if (!selectedImageName) return "imagen seleccionada";
    const suffix = formatImageSuffix(selectedImageParsedDate, selectedImageParsedTime, selectedImageParsedMachineCode) || "";
    return `${selectedImageName}${suffix}`;
  };

  if (!selectedOriginalImageSubject) {
    return (
      <p className="text-gray-400 text-center py-4">
        Selecciona una imagen del navegador para aplicar ediciones rápidas.
      </p>
    );
  }

  if (quickEditActions.length === 0) {
    return (
      <p className="text-gray-400 text-center py-4">
        No hay ediciones rápidas disponibles. Puedes definir algunas en 'Gestionar Edits Personalizados'.
      </p>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-300 mb-3">
        Aplica una transformación a la imagen actual:
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {quickEditActions.map(action => (
          <button
            key={action.id}
            onClick={() => onApplyQuickEdit(action)}
            onMouseEnter={() => onDebugHover?.(`Quick Edit Button: ${action.label}`)}
            onMouseLeave={() => onDebugHover?.(null)}
            disabled={isProcessing || !selectedOriginalImageSubject}
            className={quickEditButtonClasses + (action.isCustom ? " bg-teal-600 hover:bg-teal-700 focus:ring-teal-500" : "")}
            title={(action.promptGenerator(selectedOriginalImageSubject || "selected image") || "Error de generación de prompt").substring(0, 150) + "..."}
          >
            {isProcessing ? <LoadingSpinner size="sm" /> : action.label}
            {action.isCustom && <span className="ml-1 text-xs opacity-75">(Personalizado)</span>}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Las ediciones rápidas usan la descripción de "{getSelectedImageDisplayNameWithSuffix()}" (o su nombre si no hay descripción) como base para generar una nueva imagen con el estilo elegido.
      </p>
    </div>
  );
};

export default QuickEditPanel;
